'use client';

import { useMemo, useState } from 'react';

import { DEFAULT_PAGE_SIZE } from '@/constants/variables';

type UseCursorPaginationProps = {
  initialCursor?: string;
  itemsPerPage?: number;
  totalCount?: number;
};

function useCursorPagination({
  initialCursor = '',
  itemsPerPage = DEFAULT_PAGE_SIZE,
  totalCount,
}: UseCursorPaginationProps) {
  const [cursors, setCursors] = useState<string[]>([initialCursor]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);

  const totalPages = useMemo(() => {
    if (typeof totalCount !== 'number') {
      return 1;
    }
    return Math.ceil(totalCount / itemsPerPage);
  }, [totalCount, itemsPerPage]);

  const handlePageChange = (
    newPage: number,
    pageInfo?: {
      hasNextPage?: boolean;
      endCursor?: string | null;
      totalCount?: number;
    },
  ) => {
    const newIndex = newPage - 1;

    if (
      newIndex >= cursors.length
      && pageInfo?.hasNextPage === true
      && pageInfo.endCursor !== null
      && typeof pageInfo.endCursor === 'string'
    ) {
      setCursors([...cursors, pageInfo.endCursor]);
    }

    setCurrentCursorIndex(newIndex);
  };

  const resetPagination = () => {
    setCursors([initialCursor]);
    setCurrentCursorIndex(0);
  };

  return {
    cursors,
    currentCursorIndex,
    currentPage: currentCursorIndex + 1,
    totalPages,
    handlePageChange,
    resetPagination,
  };
}

export default useCursorPagination;
